// Time-based progress formula (replaces stage-checkpoint approach).
// Components call computeProgress at render time; the bar advances naturally
// as React re-renders (driven by the existing 2.5 s poll updating `stage`).
export const PROCESSING_SPEED_FACTOR = 4;   // AssemblyAI processes at ~4× realtime
export const DEFAULT_ESTIMATED_SEC = 300;    // fallback: 5 min when audio duration unknown

export function computeProgress(
  startedMs: number,
  estimatedSec: number,
): number {
  const denom = !estimatedSec || estimatedSec <= 0 ? DEFAULT_ESTIMATED_SEC : estimatedSec;
  const elapsed = (Date.now() - startedMs) / 1000;
  return Math.min(90, Math.max(0, Math.round((elapsed / denom) * 100)));
}

// Display-time progress: when the backend has emitted a "done/complete"
// stage/message string but `status` hasn't yet transitioned to 'completed'
// in the FE context (1–2 poll cycles before auto-redirect), force the bar
// to 100 so the UI doesn't read "Done! · 87%". Falls through to the
// time-based formula in all other cases.
export function computeDisplayProgress(
  startedMs: number,
  estimatedSec: number,
  stage?: string | null,
): number {
  if (stage) {
    const s = stage.toLowerCase();
    if (s.includes('done') || s.includes('complete')) return 100;
  }
  return computeProgress(startedMs, estimatedSec);
}

// (preserved unchanged from 0f2da52 — Bug 7+8b)
const STAGE_SUBTITLES: Array<[string, string]> = [
  ['submitted', 'Queued for processing'],
  ['queued', 'Queued for processing'],
  ['download', 'Fetching audio file'],
  ['transcrib', 'Converting sound to words'],
  ['speaker', 'Identifying who said what'],
  ['identify', 'Identifying who said what'],
  ['naming', 'Identifying who said what'],
  ['saving', 'Saving your transcript'],
  ['finaliz', 'Saving your transcript'],
  ['complete', 'Finishing up'],
];

const DEFAULT_SUBTITLE = 'Processing your file\u2026';

export function getStageSubtitle(status: string): string {
  if (!status) return DEFAULT_SUBTITLE;
  const s = status.toLowerCase();
  if (s.includes('retry')) return DEFAULT_SUBTITLE;
  for (const [keyword, subtitle] of STAGE_SUBTITLES) {
    if (s.includes(keyword)) return subtitle;
  }
  return DEFAULT_SUBTITLE;
}
